import intersection from 'helpers/walls/intersection'

/**
 * Computes all intersections between the FoV outline and the wall when exactly
 * one wall endpoint is in the FoV
 *
 * @param {Object} args - Object to be deconstructed which contains arguments
 * @param {google.maps.Point} args.innerPoint - The inner wall endpoint
 * @param {google.maps.Point} args.outerPoint - The outer wall endpoint
 * @param {google.maps.Point} args.cameraPoint - Point corresponding to the camera marker
 * @param {google.maps.Point} args.leftPoint - Point corresponding to the left angle marker
 * @param {google.maps.Point} args.rightPoint - Point corresponding to the right angle marker
 * @return {Object} The intersections among all three FoV segments
 */

export default function findIntersections({ innerPoint, outerPoint, cameraPoint, leftPoint, rightPoint }) {
  const leftIntersection = intersection(innerPoint, outerPoint, cameraPoint, leftPoint)
  const rightIntersection = intersection(innerPoint, outerPoint, cameraPoint, rightPoint)
  const endIntersection = intersection(innerPoint, outerPoint, leftPoint, rightPoint)

  return {
    left: leftIntersection.in ? leftIntersection : null,
    right: rightIntersection.in ? rightIntersection : null,
    end: endIntersection.in ? endIntersection : null
  }
}

/**
 * Computes all intersections between the FoV outline and the wall when both
 * wall endpoints are outside the FoV
 *
 * @param {Object} args - Object to be deconstructed which contains arguments
 * @param {google.maps.Point} args.innerPoint - The inner wall endpoint
 * @param {google.maps.Point} args.outerPoint - The outer wall endpoint
 * @param {google.maps.Point} args.cameraPoint - Point corresponding to the camera marker
 * @param {google.maps.Point} args.leftPoint - Point corresponding to the left angle marker
 * @param {google.maps.Point} args.rightPoint - Point corresponding to the right angle marker
 * @return {Object} The intersections among all three FoV segments
 */

export function findThroughIntersections({ innerPoint, outerPoint, cameraPoint, leftPoint, rightPoint }) {
  let leftIntersection = intersection(innerPoint, outerPoint, cameraPoint, leftPoint, 1.0)
  let rightIntersection = intersection(innerPoint, outerPoint, cameraPoint, rightPoint, 1.0)
  let endIntersection = intersection(innerPoint, outerPoint, leftPoint, rightPoint, 1.0)

  if (leftIntersection.in && rightIntersection.in) {
    const fudge = leftIntersection.ua < rightIntersection.ua ? 0.99 : 1.01
    leftIntersection = intersection(innerPoint, outerPoint, cameraPoint, leftPoint, fudge)
    rightIntersection = intersection(innerPoint, outerPoint, cameraPoint, rightPoint, 2 - fudge)
  } else if (leftIntersection.in && endIntersection.in) {
    const fudge = leftIntersection.ua < endIntersection.ua ? 0.99 : 1.01
    leftIntersection = intersection(innerPoint, outerPoint, cameraPoint, leftPoint, fudge)
    endIntersection = intersection(innerPoint, outerPoint, leftPoint, rightPoint, 2 - fudge)
  } else {
    const fudge = rightIntersection.ua < endIntersection.ua ? 0.99 : 1.01
    rightIntersection = intersection(innerPoint, outerPoint, cameraPoint, rightPoint, fudge)
    endIntersection = intersection(innerPoint, outerPoint, leftPoint, rightPoint, 2 - fudge)
  }

  return {
    left: leftIntersection.in ? leftIntersection : null,
    right: rightIntersection.in ? rightIntersection : null,
    end: endIntersection.in ? endIntersection : null
  }
}



// WEBPACK FOOTER //
// ./src/helpers/walls/findIntersections.js